import React from 'react';
import {
  AppBar as MuiAppBar,
  AppBarProps as MuiAppBarProps,
  Drawer as MuiDrawer,
  Box,
  CssBaseline,
  IconButton,
  Toolbar,
  styled,
  Typography,
  Divider,
  List,
  DrawerProps as MuiDrawerProps,
  Avatar,
  Menu,
  MenuItem,
  InputBase,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout as LogoutIcon,
  Search as SearchIcon,
  ChevronLeft
} from '@mui/icons-material';
import { Navigate, Outlet, Link, useNavigate } from 'react-router-dom';
import auth from 'services/auth';
import { MainListItems, AdminListItems } from 'components/Sidebar/Sidebar';
import useIsMobile from 'hooks/useIsMobile';
import LocalStorageService from 'services/LocalStorageService';
import NotificationMenu from 'components/common/NotificationMenu';
import useConfiguration from 'hooks/useConfiguration';

const desktopDrawerWidth = 240;
const mobileDrawerWidth = 240;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
  isMobile?: boolean;
}

interface DrawerProps extends MuiDrawerProps {
  isMobile?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile'
})<AppBarProps>(({ theme, open, isMobile }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open &&
    !isMobile && {
    marginLeft: desktopDrawerWidth,
    width: `calc(100% - ${desktopDrawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  })
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'isMobile'
})<DrawerProps>(({ theme, open, isMobile }) => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    backgroundColor: '#f4f5f7',
    borderRight: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    boxSizing: 'border-box',
    width: isMobile ? mobileDrawerWidth : desktopDrawerWidth,
    ...(!open &&
      !isMobile && {
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
      }),
      width: theme.spacing(8)
    })
  }
}));

const SearchBox = styled('form')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flex: 1,
  minWidth: 0,
  maxWidth: 560,
  width: '100%',
  margin: '0 auto',
  padding: '6px 12px',
  borderRadius: 8,
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('sm')]: {
    maxWidth: 'none',
    margin: 0,
    padding: '4px 8px'
  }
}));

const AdminRoute = () => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [open, setOpen] = React.useState(() => !isTablet);
  const [search, setSearch] = React.useState('');
  const [userMenu, setUserMenu] = React.useState<null | HTMLElement>(null);
  const enableNotifications = useConfiguration<boolean>('enable_notifications');

  const toggleDrawer = () => {
    setOpen((prev) => !prev);
  };

  const toggleMobileDrawer = () => {
    isMobile && setOpen((prev) => !prev);
  };

  const isAdmin = auth.isAdmin();
  const displayName = LocalStorageService.getUserName();

  if (!auth.isAuthenticated()) {
    return <Navigate to="/login" />;
  }

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const query = search.trim();
    navigate(query ? `/my_events?q=${encodeURIComponent(query)}` : '/my_events');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />

      <AppBar position="fixed" open={open} isMobile={isMobile} className="admin-appbar">
        <Toolbar sx={{ minHeight: { xs: '56px !important', sm: '64px !important' }, gap: { xs: 1, sm: 2 }, px: { xs: 1, sm: 2 } }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{ display: open && !isMobile ? 'none' : 'inline-flex' }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            component={Link}
            to="/"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              display: { xs: 'none', md: 'block' }
            }}
          >
            LiveZone Admin
          </Typography>

          <SearchBox onSubmit={handleSearch}>
            <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <InputBase
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={isMobile ? 'Search...' : 'Search events, competitors, devices...'}
              sx={{ flex: 1, fontSize: { xs: 13, sm: 14 }, minWidth: 0 }}
            />
          </SearchBox>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: { xs: 0, sm: 'auto' }, flexShrink: 0 }}>
            {enableNotifications && <NotificationMenu size={isMobile ? 'small' : 'medium'} />}
            <Box
              onClick={(e) => setUserMenu(e.currentTarget)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                px: { xs: 0.25, sm: 1 },
                py: 0.5,
                borderRadius: 1,
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main, fontSize: 13, fontWeight: 700 }}>
                {displayName.slice(0, 1).toUpperCase()}
              </Avatar>
              <Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' }, fontWeight: 600 }}>
                {displayName}
              </Typography>
            </Box>
            <Menu anchorEl={userMenu} open={Boolean(userMenu)} onClose={() => setUserMenu(null)}>
              <MenuItem disabled>
                <AccountCircle sx={{ mr: 1 }} />
                {isAdmin ? 'Admin' : displayName}
              </MenuItem>
              <MenuItem component={Link} to="/logout" onClick={() => setUserMenu(null)}>
                <LogoutIcon sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={open}
        isMobile={isMobile}
        onClose={toggleDrawer}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: { width: isMobile ? mobileDrawerWidth : desktopDrawerWidth },
          className: 'admin-sidebar'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: open ? 'space-between' : 'center',
            px: open ? 2 : 1,
            minHeight: 64,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, display: open ? 'block' : 'none' }}>
            LiveZone
          </Typography>
          <IconButton edge="start" color="inherit" onClick={toggleDrawer} size="small">
            {open ? <ChevronLeft /> : <MenuIcon />}
          </IconButton>
        </Box>

        <List component="nav" sx={{ flexGrow: 1, py: 1 }}>
          <MainListItems onClickLink={toggleMobileDrawer} isOpen={open} />
          {isAdmin && <AdminListItems onClickLink={toggleMobileDrawer} isOpen={open} />}
        </List>

        {!isMobile && (
          <React.Fragment>
            <Divider />
            <Box sx={{ p: 1 }}>
              <ListItemCollapse open={open} onClick={toggleDrawer} />
            </Box>
          </React.Fragment>
        )}
      </Drawer>

      <Box
        component="main"
        sx={{
          backgroundColor: theme.palette.background.default,
          flexGrow: 1,
          minHeight: '100vh',
          minWidth: 0,
          width: '100%',
          overflow: 'auto'
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
        <Box
          sx={{
            width: '100%',
            maxWidth: 'none',
            mx: 0,
            mt: { xs: 1, md: 2 },
            mb: { xs: 2, md: 3 },
            px: { xs: 1.5, sm: 2, md: 3 },
            minHeight: { xs: 'calc(100vh - 80px)', sm: 'calc(100vh - 96px)' }
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

const ListItemCollapse = ({ open, onClick }: { open: boolean; onClick: () => void }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: open ? 1.5 : 0,
      py: 1,
      borderRadius: 1,
      cursor: 'pointer',
      justifyContent: open ? 'flex-start' : 'center',
      color: 'text.secondary',
      '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' }
    }}
  >
    <ChevronLeft sx={{ transform: open ? 'none' : 'rotate(180deg)', fontSize: 20 }} />
    {open && (
      <Typography variant="body2" fontWeight={600}>
        Collapse
      </Typography>
    )}
  </Box>
);

export default AdminRoute;
